import axios from 'axios';

import { env } from '@/shared/configs';

import { responseErrorHandler, responseSuccessHandler, withAuthRequestHandler, withLocaleRequestHandler } from './handlers';

import type { CreateAxiosDefaults } from 'axios';

const createAxiosInstance = (config: CreateAxiosDefaults) => axios.create(config);

/**
 * 요청 타임아웃 (TRAD-2145).
 *
 * **15초 — 실측 최악(느린 3G 에뮬 1.20초)의 12.5배, 그리고 최단 폴링 주기(30초)의 절반.**
 *
 * 이 값이 없으면(axios 기본 `timeout: 0`) 요청이 영영 pending 이라 React Query 가 계속
 * `isLoading` 이고 **스켈레톤이 무한히 유지된다.** 2026-07-30 운영 3시간 장애의 초기 증상이
 * 정확히 이것이었고, 7개 화면 전부가 해당했다(측정: `docs/planning/frontend-outage-resilience-2026-07-31.md` §1-7).
 *
 * ── 값의 근거 (추측 아님) ──────────────────────────────────────────────────
 * dev.xtrading.kr 에 대해 앱이 실제로 보내는 요청의 소요시간을 브라우저에서 측정(표본 41건/조건):
 *   정상망      median 0.07초 · p95 0.16초 · **max 0.43초**
 *   느린3G 에뮬  median 0.58초 · p95 0.92초 · **max 1.20초**  (지연 400ms · 400kbps)
 * → 하한은 "가장 느린 정상 요청"이 정한다. 1.20초 × 12.5 = 15초.
 * → 상한은 **최단 폴링 주기 30초**(김프)가 정한다. 타임아웃이 주기보다 길면 이전 요청이
 *   끝나기 전에 다음 폴링이 나가 요청이 겹친다. 15초는 그 절반이라 겹칠 수 없다.
 *
 * ⚠️ 짧게 잡는 방향이 위험하다. 너무 짧으면 **느린 네트워크의 정상 요청을 실패로 만든다** —
 * 무한 로딩을 반대 방향의 문제로 바꾸는 것일 뿐이다. 에뮬레이션은 실제 최악(혼잡·패킷손실·
 * 터널)보다 낙관적이므로 여유를 크게 뒀다.
 *
 * ── 왜 단일 전역값인가 ────────────────────────────────────────────────────
 * 실시간 시세는 **WebSocket** 이라(`useUpbitWebSocket` 등) 이 axios 타임아웃의 영향을 받지 않는다.
 * axios 로 폴링하는 것은 김프 30초 · 마켓 60초 · 글로벌 5분 · 환율 1시간뿐이고 전부 15초보다
 * 길다. 즉 **폴링용 값을 따로 둘 이유가 없다.** 화면별로 쪼개면 값이 갈려 관리 비용만 늘어난다.
 *
 * 값을 바꾸려면 위 측정을 다시 하고 근거를 여기 갱신할 것.
 */
const REQUEST_TIMEOUT_MS = 15_000;

const defaultConfig: CreateAxiosDefaults = {
  timeout: REQUEST_TIMEOUT_MS,
  baseURL: typeof window !== 'undefined' ? window.location.origin : '',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    'x-appkey': env.appKey,
    'X-App-Platform': 'WEB',
  },
};

export const apiWithoutAuth = createAxiosInstance(defaultConfig);
export const apiWithAuth = createAxiosInstance(defaultConfig);

// TRAD-3662 — Accept-Language 는 토큰 유무와 무관하게 **두 인스턴스 모두**에 붙인다(에러 message 로케일은 비로그인 요청에도 적용).
apiWithoutAuth.interceptors.request.use(withLocaleRequestHandler);
apiWithAuth.interceptors.request.use(withLocaleRequestHandler);
apiWithAuth.interceptors.request.use(withAuthRequestHandler);
apiWithAuth.interceptors.response.use(responseSuccessHandler, responseErrorHandler);
